import {
  ActionIdeaForm,
  CreateIdeaFormFieldTypes,
  CreateIdeaFormRef,
} from './action-idea-form.tsx'
import { FillStrategies } from './action-idea-form.ts'
import { UserInfo } from '@/apis/auth/auth.def.ts'
import { useAppSelector } from '@/redux/hooks.ts'
import { selectUser } from '@/features/user/user.slice.ts'
import { FormInstance } from 'antd/lib/index'
import ConfigProvider from 'antd/es/config-provider/index'
import IdeyaModal from '@/common/antd/ideya-modal.tsx'
import { IdeyaSpinner } from '@/common/components/ideya-spinner.tsx'
import { useGetBookDetailsQuery } from '@/apis/book/book-api.api.ts'
import { useGetIdeaDetailQuery } from '@/apis/idea/idea-api.api.ts'
import { getErrorDescription } from '@/redux/utils.ts'
import { NEW_RELIC, newRelic } from '@/utils/new-relic.ts'
import { staticNotification as notification } from '@/common/antd/static-notification.ts'
import { useEffect, useRef, useState } from 'react'
import { LiquidateIdeaForm } from './liquidate-idea-form.tsx'
import { useFixedBreakpoint } from '@/common/hooks/use-is-mobile.ts'
import { useTranslation } from 'react-i18next'
import { IdeaDirectionType } from '@/enums/idea-list.enum.ts'
import { ActionIdeaConfirmForm } from './action-idea-confirm-form.tsx'
import { FadeView } from '@/common/anim/fade-view.tsx'
import './action-idea-modal.scss'

// Duration of each fade. The incoming view waits this long so it only starts
// fading in after the outgoing view has finished fading out.
// Aligned with antd's motionDurationSlow (--ant-motion-duration-slow = 0.3s).
const FADE_DURATION = 300

interface ActionIdeaModalProps {
  showActionModal: boolean
  showLiquidateModal: boolean
  setShowLiquidatedModal: (v: boolean) => void
  form: FormInstance<CreateIdeaFormFieldTypes>
  handleCloseModal: () => void
  onIdeaActioned?: () => void
  editIdeaId?: string | null
  setEditIdeaId: (id: string | null) => void
}

export const ActionIdeaModal: React.FC<ActionIdeaModalProps> = ({
  showActionModal,
  showLiquidateModal,
  setShowLiquidatedModal,
  form,
  handleCloseModal,
  onIdeaActioned,
  editIdeaId,
  setEditIdeaId,
}) => {
  const { t } = useTranslation()
  const userInfo: UserInfo = useAppSelector(selectUser)
  const [showConfirmIdeaModal, setShowConfirmIdeaModal] =
    useState<boolean>(false)
  const [confirmTicker, setConfirmTicker] = useState<{
    tickerLabel: string
    tickerCurrency?: string
    hasTargetMarketValueEdited: boolean
  }>({ tickerLabel: '', hasTargetMarketValueEdited: false })
  // Keep the confirm form out of the open commit; mount it only once the user
  // has reached the confirm step, then keep it mounted so its fade still runs.
  const [hasShownConfirm, setHasShownConfirm] = useState<boolean>(false)
  // Defer the heavy form mount by a frame so the modal shell paints first.
  const [formReady, setFormReady] = useState<boolean>(false)

  const {
    data,
    isLoading,
    refetch: refetchGetBookDetailsQuery,
  } = useGetBookDetailsQuery(
    { id: userInfo.bookId },
    { skip: !userInfo.bookId }
  )

  const {
    currentData: editData,
    isFetching: isFetchingEdit,
    isError: isEditError,
    error: editError,
  } = useGetIdeaDetailQuery(editIdeaId ?? '', {
    skip: !editIdeaId,
  })

  // Show the spinner on first load and when switching to a different idea
  // (currentData is undefined until the new arg resolves), but not on a
  // post-submit refetch of the same idea (currentData keeps the old value).
  const isModalLoading = isLoading || (isFetchingEdit && !editData)

  useEffect(() => {
    if (!showActionModal || isModalLoading) return
    // Let the Dialog shell paint, then mount the field grid on the next frame
    // so the open cost is split across two commits instead of one long one.
    const raf = requestAnimationFrame(() => setFormReady(true))
    return () => cancelAnimationFrame(raf)
  }, [showActionModal, isModalLoading])

  useEffect(() => {
    // Only touch the form while the modal (and thus the <Form>) is mounted.
    // The form is rendered only when the modal is open, not loading, and the
    // deferred mount has run; calling form methods while it is unmounted
    // (destroyOnClose) warns that the useForm instance is not connected.
    if (!showActionModal || isModalLoading || !formReady) return

    if (!editIdeaId) {
      form.resetFields()
      return
    }

    if (editData?.data) {
      form.resetFields()
      form.setFieldsValue({
        figi: editData.data.ticker?.figi,
        direction: editData.data.direction as IdeaDirectionType,
        targetMarketValue: Math.round(editData.data.targetMarketValue),
        target: editData.data.target,
        stop: editData.data.stop,
        fillStrategy:
          // Previous fill strategy was 15min-VWAP, hardcode it to 1hr-vwap now
          editData.data.fillStrategy === '15min-VWAP' ||
          editData.data.fillStrategy === '1hour-VWAP'
            ? FillStrategies[0].value
            : editData.data.fillStrategy,
        auto_fill: editData.data.auto_fill,
        tradeReason:
          editData.data.tradeReason
            ?.split(',')
            .map((reason) => reason.trim()) ?? null,
        horizon: editData.data.horizon,
        conviction: editData.data.conviction,
        comment: editData.data.description,
      })
    }
  }, [showActionModal, isModalLoading, formReady, editIdeaId, editData, form])

  const createIdeaFormRef = useRef<CreateIdeaFormRef>(null)
  const screens = useFixedBreakpoint()
  const isMobile = !screens.md

  // Trigger the close only; the fields stay populated so the data remains
  // visible while the modal animates out.
  const handleClose = () => {
    handleCloseModal()
  }

  // Clear everything after the close animation finishes, so the user never
  // sees the form blank out before the modal disappears.
  const handleAfterClose = () => {
    setFormReady(false)
    setShowConfirmIdeaModal(false)
    setHasShownConfirm(false)
    setEditIdeaId(null)
    form.resetFields()
    createIdeaFormRef.current?.resetFormState()
  }

  // Read through a ref so the error effect below doesn't re-run (and re-notify)
  // when this handler's identity changes.
  const handleCloseRef = useRef(handleClose)
  handleCloseRef.current = handleClose

  // The detail fetch failed, so there is nothing to edit. Report it, tell the
  // user, and close instead of leaving an empty form open.
  useEffect(() => {
    if (!showActionModal || !isEditError) return

    const description = getErrorDescription(editError)
    newRelic.recordCustomEvent('GetIdeaDetailFailed', {
      element: NEW_RELIC.ERROR.ideaDetail,
      event: description,
    })
    notification.error({
      message: 'Error',
      description,
      placement: 'topRight',
    })
    handleCloseRef.current()
  }, [showActionModal, isEditError, editError])

  return (
    <>
      <IdeyaModal
        open={showActionModal}
        title={
          showConfirmIdeaModal
            ? t('idea_list.idea_modal.confirm_idea_form.confirm_your_idea')
            : editIdeaId
              ? t('idea_list.idea_modal.action_idea_modal.edit_idea')
              : t('idea_list.idea_modal.action_idea_modal.create_idea')
        }
        // Keep the Dialog shell mounted so reopening skips re-mounting the
        // modal scaffolding; the form itself still unmounts via formReady.
        destroyOnHidden={false}
        isForm={true}
        width={isMobile ? '98vw' : 1300}
        onCancel={handleClose}
        afterClose={handleAfterClose}
        style={isMobile ? { top: 4, padding: 4 } : {}}
        className={'action-idea-modal'}
      >
        {isModalLoading || !formReady ? (
          <div
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <IdeyaSpinner />
          </div>
        ) : (
          // Disable antd's button ripple inside the modal; the Wave effect
          // mounts its own React roots and adds measurable open cost.
          <ConfigProvider wave={{ disabled: true }}>
            <div className={'action-idea-modal__forms'}>
              <FadeView
                visible={!showConfirmIdeaModal}
                duration={FADE_DURATION}
                delay={FADE_DURATION * 0.3}
                style={{ gridArea: '1 / 1' }}
              >
                <ActionIdeaForm
                  key={editIdeaId ?? 'create'}
                  ref={createIdeaFormRef}
                  form={form}
                  onCancel={handleClose}
                  onConfirm={(ticker) => {
                    setConfirmTicker(ticker)
                    setHasShownConfirm(true)
                    setShowConfirmIdeaModal(true)
                  }}
                  buyingPower={data?.data?.buyingPower}
                  usedBuyingPower={data?.data?.usedBuyingPower}
                  userInfo={userInfo}
                  onIdeaActioned={() => {
                    onIdeaActioned?.()
                    refetchGetBookDetailsQuery()
                  }}
                  onEditIdea={(ideaId: string) => setEditIdeaId(ideaId)}
                  mode={editIdeaId ? 'edit' : 'create'}
                  ideaDetail={editData?.data}
                  onIdeaLiquidated={() => setShowLiquidatedModal(true)}
                />
              </FadeView>
              {hasShownConfirm && (
                <FadeView
                  visible={showConfirmIdeaModal}
                  duration={FADE_DURATION}
                  delay={FADE_DURATION * 0.3}
                  style={{ gridArea: '1 / 1' }}
                >
                  <ActionIdeaConfirmForm
                    form={form}
                    tickerLabel={confirmTicker.tickerLabel}
                    tickerCurrency={confirmTicker.tickerCurrency}
                    onCancel={() => setShowConfirmIdeaModal(false)}
                    onSubmit={() => form.submit()}
                    ideaDetail={editData?.data}
                    hasTargetMarketValueEdited={
                      confirmTicker.hasTargetMarketValueEdited
                    }
                  />
                </FadeView>
              )}
            </div>
          </ConfigProvider>
        )}
      </IdeyaModal>
      <IdeyaModal
        open={showLiquidateModal}
        onCancel={() => setShowLiquidatedModal(false)}
        title={t('idea_list.idea_modal.action_idea_modal.liquidate_idea')}
        isForm={true}
        width={isMobile ? '95vw' : 500}
        zIndex={1500}
        style={isMobile ? { top: 8, padding: 8 } : {}}
      >
        <LiquidateIdeaForm
          ideaId={editIdeaId ?? ''}
          fillStrategy={
            // Only read the shared form while it is mounted (modal open, not
            // loading); reading it after destroyOnClose unmounts the <Form>
            // warns that the useForm instance is not connected.
            (showActionModal && !isModalLoading
              ? form.getFieldValue('fillStrategy')
              : undefined) ??
            editData?.data.fillStrategy ??
            ''
          }
          onIdeaActioned={onIdeaActioned}
          onCancel={() => setShowLiquidatedModal(false)}
          onSuccess={() => {
            setShowLiquidatedModal(false)
            handleClose()
          }}
        />
      </IdeyaModal>
    </>
  )
}
